import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Shield, Award, Users, CheckCircle } from 'lucide-react';

const TEAM_IMAGE = 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/qskuuchf_FB_IMG_1771192477688.jpg';

const coreValues = [
  {
    icon: Clock,
    title: 'Fast Response Times',
    description: 'We understand plumbing emergencies don\'t wait. That\'s why we prioritize quick response times to get your issues resolved fast.',
  },
  {
    icon: Users,
    title: 'Professional, Courteous Service',
    description: 'Our team treats your home with respect, arriving on time, explaining the work clearly, and leaving your space clean.',
  },
  {
    icon: Shield,
    title: 'Licensed & Insured Workmanship',
    description: 'All work is performed by licensed journeymen plumbers with full insurance coverage for your protection and peace of mind.',
  },
  {
    icon: Award,
    title: 'Emergency Service Available',
    description: 'Plumbing problems don\'t follow a schedule. We offer 24/7 emergency service for urgent situations.',
  },
];

export const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#005B96]" data-testid="about-hero">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581578731117-104f2a417954?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10" />
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-semibold mb-6">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              Your Local Plumbing & Heating Experts
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Serving Yarmouth, Nova Scotia with reliable, professional plumbing, heating, and water filtration services.
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-content">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted Plumbers Serving Yarmouth Since Day One
              </h2>
              <div className="prose prose-lg text-gray-600">
                <p className="mb-6 leading-relaxed">
                  We are a locally owned plumbing and heating company in Yarmouth NS, serving homeowners and businesses with reliable, professional service. Our licensed journeymen provide plumbing repairs, heating installations, in-floor heating, pump installation, sewer rooting, and water filtration solutions.
                </p>
                <p className="mb-6 leading-relaxed">
                  At Duncan's Plumbing Heating & Filtration, we take pride in our commitment to quality workmanship and customer satisfaction. Whether you need emergency repairs or planned installations, we're here to help with fast, honest service.
                </p>
                <p className="leading-relaxed">
                  Our team understands the unique needs of Nova Scotia homeowners, from harsh winters requiring reliable heating to the importance of clean water filtration. We're not just service providers – we're your neighbors, committed to keeping Yarmouth homes comfortable and safe.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <a
                  href="tel:902-746-0554"
                  className="inline-flex items-center justify-center gap-2 bg-[#005B96] text-white px-8 py-4 rounded-full font-bold hover:bg-[#004a7a] transition-all shadow-lg hover:shadow-xl active:scale-95"
                  data-testid="about-cta-call"
                >
                  <Phone size={20} />
                  <span>Contact Us – 902-746-0554</span>
                </a>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#005B96] text-[#005B96] px-8 py-4 rounded-full font-bold hover:bg-[#005B96] hover:text-white transition-all"
                  data-testid="about-cta-services"
                >
                  <span>Our Services</span>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={TEAM_IMAGE}
                alt="Duncan's Plumbing team"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="core-values">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-gray-600 text-lg">
              We're committed to providing exceptional service built on core values that have earned the trust of Yarmouth homeowners.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {coreValues.map((value) => (
              <div
                key={value.title}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all border border-slate-100"
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#E6F2FA] rounded-xl flex items-center justify-center">
                    <value.icon size={28} className="text-[#005B96]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 md:py-28 bg-white" data-testid="service-area">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="bg-[#E6F2FA] rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Proudly Serving Yarmouth & Surrounding Areas
                </h2>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                  We provide plumbing, heating, and water filtration services throughout Yarmouth, Digby and Shelburne Counties, including:
                </p>
                <ul className="grid grid-cols-2 gap-3">
                  {['Yarmouth County', 'Digby County', 'Shelburne County', 'Clare', 'Pubnico', 'Meteghan'].map((area) => (
                    <li key={area} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle size={18} className="text-[#005B96]" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center md:text-right">
                <p className="text-gray-600 mb-4">Ready to schedule service?</p>
                <a
                  href="tel:902-746-0554"
                  className="inline-flex items-center gap-2 bg-[#005B96] text-white px-8 py-4 rounded-full font-bold hover:bg-[#004a7a] transition-all shadow-lg hover:shadow-xl active:scale-95"
                  data-testid="service-area-cta"
                >
                  <Phone size={20} />
                  <span>Call 902-746-0554</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="sticky-cta md:hidden">
        <a
          href="tel:902-746-0554"
          className="flex items-center justify-center gap-2 bg-[#005B96] text-white w-full py-4 rounded-full font-bold"
        >
          <Phone size={20} />
          <span>Call Now – 902-746-0554</span>
        </a>
      </div>
    </div>
  );
};

export default About;
